import type { SupabaseClient } from '@supabase/supabase-js';

/**
 * Get back into an account with a player name and a recovery code.
 *
 * For every under-14 account this is the ONLY route back, because there is no
 * email to send a reset link to. The recovery-code screen and the published
 * documents both promise it works.
 *
 * Spec: docs/superpowers/specs/2026-09-12-privacy-compliance-design.md §5.
 */

export type RecoverResult = { ok: true; recoveryCode: string } | { ok: false; error: string };

function functionsUrl(): string | null {
  const base = import.meta.env.VITE_SUPABASE_URL;
  return base ? `${String(base).replace(/\/$/, '')}/functions/v1/recover-account` : null;
}

export async function recoverAccount(
  client: SupabaseClient,
  username: string,
  recoveryCode: string,
  newPassword: string,
): Promise<RecoverResult> {
  const url = functionsUrl();
  if (!url) return { ok: false, error: 'Accounts are not available in this build' };

  let response: Response;
  try {
    response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        // BOTH headers are required. Supabase verifies a JWT on Edge Functions
        // by default, and an apikey header alone is refused with 401 — which
        // local `functions serve --no-verify-jwt` hides. The anon key is a
        // valid JWT and is what an unauthenticated caller presents.
        apikey: String(import.meta.env.VITE_SUPABASE_ANON_KEY ?? ''),
        Authorization: `Bearer ${String(import.meta.env.VITE_SUPABASE_ANON_KEY ?? '')}`,
      },
      body: JSON.stringify({
        username: username.trim(),
        recoveryCode: recoveryCode.trim().toUpperCase(),
        newPassword,
      }),
    });
  } catch {
    return { ok: false, error: 'Could not reach the server. Check your connection.' };
  }

  let body: { ok?: boolean; error?: string; recoveryCode?: string; session?: unknown };
  try {
    body = (await response.json()) as typeof body;
  } catch {
    return { ok: false, error: 'Could not check that code' };
  }

  if (!response.ok || !body.ok || !body.recoveryCode) {
    return { ok: false, error: body.error ?? 'That name and code did not match' };
  }

  const session = body.session as
    | { access_token: string; refresh_token: string }
    | null
    | undefined;
  if (session?.access_token && session?.refresh_token) {
    await client.auth.setSession({
      access_token: session.access_token,
      refresh_token: session.refresh_token,
    });
  }

  // The old code stopped working the moment this succeeded; the player must be
  // shown and made to acknowledge the replacement, exactly as at signup.
  return { ok: true, recoveryCode: body.recoveryCode };
}
